import { useEffect, useRef } from "react";
import { UniverseRenderer } from "./UniverseRenderer.js";
import styles from "./UniverseCanvas.module.css";

/** The universe is soft, so a lower buffer than the sparks' 2x saves fill rate unseen. */
const MAX_DPR = 1.5;
/** Longest step the clocks take, so returning to the tab doesn't jump the field. */
const MAX_DT = 0.1;

/** Full-screen background with the projection's dust universe, shared by every screen. */
export function UniverseCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = UniverseRenderer.create(canvas);
    if (!renderer) {
      canvas.hidden = true;
      return;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let last = performance.now();
    let lost = false;

    const resize = (): void => {
      renderer.resize(
        canvas.clientWidth,
        canvas.clientHeight,
        Math.min(window.devicePixelRatio || 1, MAX_DPR),
      );
      // A still frame for reduced motion; the loop redraws anyway otherwise.
      if (!lost) renderer.render(0);
    };

    // rAF stops while the tab is hidden, so the loop pauses with it.
    const tick = (now: number): void => {
      const dt = Math.min((now - last) / 1000, MAX_DT);
      last = now;
      if (!motionQuery.matches) renderer.render(dt);
      frame = requestAnimationFrame(tick);
    };

    const onLost = (event: Event): void => {
      event.preventDefault();
      lost = true;
      cancelAnimationFrame(frame);
      // The page background matches the backdrop, so the screen just loses its dust.
      canvas.hidden = true;
    };

    resize();
    window.addEventListener("resize", resize);
    canvas.addEventListener("webglcontextlost", onLost);
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("webglcontextlost", onLost);
      if (!lost) renderer.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />;
}
